import type { ExecutiveSnapshotDto } from '@inference.engine/api-client';
import { AnalysisMarkdown, AnalysisSubHeader } from '../../../components';
import { BorderedChipGrid } from './BorderedChipGrid';
import { useTranslation } from 'react-i18next';

import { hasContent } from '../../../utils/hasContent';

interface BusinessModelProps {
  data?: ExecutiveSnapshotDto['businessModel'];
  brandName: string;
}

export const hasBusinessModel = (data: BusinessModelProps['data']) =>
  hasContent([data?.primaryModel, data?.revenueStreams, data?.valueProposition, data?.keyPartners]);

export const BusinessModel = ({ data, brandName }: BusinessModelProps) => {
  const { t } = useTranslation('analysis');

  if (!data || !hasBusinessModel(data)) return null;

  const hasLeftColumn = Boolean(data.primaryModel || data.revenueStreams?.length);

  return (
    <div className="flex flex-col gap-8">
      <AnalysisSubHeader
        title={t('analysisResult.businessModel.businessModel')}
        subtitle={t('analysisResult.businessModel.howBrandCreatesAndCapturesValue', { brandName })}
        subtitleTone="meta"
      />

      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-2">
        {hasLeftColumn && (
          <div className="flex flex-col gap-3">
            {data.primaryModel && (
              <BorderedBlock title={t('analysisResult.businessModel.primaryModel')}>
                <AnalysisMarkdown
                  text={data.primaryModel}
                  className="text-body-md text-foreground"
                />
              </BorderedBlock>
            )}

            {data.revenueStreams && data.revenueStreams.length > 0 && (
              <BorderedBlock title={t('analysisResult.businessModel.revenueStreams')}>
                <ul className="flex flex-col gap-1">
                  {data.revenueStreams.map((stream, i) => (
                    <li key={`${stream}-${i}`} className="flex items-start gap-3.5 pl-1.5">
                      <span className="mt-[9px] size-1.5 shrink-0 rounded-full bg-foreground-subtle" />
                      <AnalysisMarkdown text={stream} className="text-body-md text-foreground" />
                    </li>
                  ))}
                </ul>
              </BorderedBlock>
            )}
          </div>
        )}

        {/* The value proposition is the section's claim, so it sits on the tinted surface
            opposite the mechanics that deliver it. */}
        {data.valueProposition && (
          <div className="flex h-full flex-col items-center justify-center gap-3 rounded-xl bg-surface dark:bg-surface-raised px-3xl py-2xl text-center">
            <span className="text-heading-sm font-medium text-foreground">
              {t('analysisResult.businessModel.valueProposition')}
            </span>
            <AnalysisMarkdown
              text={data.valueProposition}
              className="text-body-lg text-foreground"
            />
          </div>
        )}
      </div>

      {data.keyPartners && data.keyPartners.length > 0 && (
        <div className="flex flex-col gap-2.5">
          <span className="text-label-md font-medium text-foreground-subtle">
            {t('analysisResult.businessModel.keyPartners')}
          </span>
          <BorderedChipGrid
            items={data.keyPartners}
            columns={3}
            badgeLabel={(number) => t('analysisResult.businessModel.partnerNumber', { number })}
          />
        </div>
      )}
    </div>
  );
};

const BorderedBlock = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-3 rounded-lg border border-border-light p-5">
    <span className="text-heading-sm font-medium text-foreground">{title}</span>
    {children}
  </div>
);
